import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material';

// Categorical series colors, validated for color-vision deficiency and contrast against each theme's surface. Slots are
// assigned in fixed order and follow the driver (never re-colored when others are hidden). Line/bar forms can use all
// eight; scatter plots cap at the first three (all-pairs separation).
export const SERIES_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
export const SERIES_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'];
export const MAX_SERIES = SERIES_LIGHT.length;
export const MAX_SCATTER_SERIES = 3;

/** Theme-aware chart tokens. */
export const useChartTokens = () => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  return {
    dark,
    series: dark ? SERIES_DARK : SERIES_LIGHT,
    surface: theme.palette.background.paper,
    grid: dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
    axisText: theme.palette.text.secondary,
    text: theme.palette.text.primary,
  };
};

/** Color per driver, by the driver's position in the full list (stable when some are hidden). */
export const driverColors = (drivers: string[], series: string[]) =>
  new Map(drivers.map((d, i) => [d, series[i % series.length]]));

/**
 * Width of a container, kept in sync with resizes. Uses a callback ref so it starts measuring whenever the element
 * appears (a chart can mount before its container is rendered, e.g. behind a tab or a loading state).
 */
export const useWidth = (min = 280, initial = 640) => {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(initial);
  useEffect(() => {
    if (!el) return;
    setWidth(Math.max(min, Math.floor(el.getBoundingClientRect().width)));
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(min, Math.floor(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [el, min]);
  return { ref: setEl, width };
};

export const raceKey = (r: { Date: string; Site: string }) => `${r.Date}|${r.Site}`;
export const lastName = (name: string) =>
  name
    .split(' ')
    .filter((w) => !/^(jr|sr|ii|iii)\.?$/i.test(w))
    .pop() ?? name;
